import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Card, Grid, Divider, Typography } from '@mui/material'
import { DateDayControl } from 'src/admin/core/components/date/DateDayControl'
import { Agency, Enterprise, WorkstationProductsEnum } from 'src/core/models'
import { AgencySelect, WorkstationProductSelect } from 'src/admin/core'
import { WorkstationSelect } from 'src/admin/scenes/entities/stations/components/WorkstationSelect'
import { Workstation } from 'src/admin/scenes/entities/stations/models'
import { useAppTranslation } from 'src/admin/core/hooks'
import { saveAs } from 'file-saver'
import { DownloadButton, SearchButton, ProviderSelect } from 'src/admin/core'
import {
  dateIsBeforeThanTodayMoment,
  formatJSDateToLocal,
  isFutureDate,
  isSameDate,
} from 'src/admin/core/services/dateFunctions'

import { getAppBarState } from 'src/store'
import { UserEnterpriseSelect } from 'src/admin/core/components/Select/UserEnterprisesSelect'
import { useLotsalcomFileDownloadUri } from '../../agencyBalance/hooks'
 

export interface FilterModel {
  product: string
  enterpriseId: string
  agencyId: string
  workstationId: string
  from: string
  to: string
  currency: string
  provider: string
  // Complete objects for metadata
  productObj?: { value: string; label: string }
  enterpriseObj?: Enterprise
  agencyObj?: Agency
  workstationObj?: Workstation
  providerObj?: { value: string; label: string }
}

export const today = new Date()

const { userEnterprise } = getAppBarState().appState

export const initialFilters = {
  product: '',
  enterpriseId: '',
  agencyId: '',
  workstationId: '',
  from: formatJSDateToLocal(today) + 'T00:00:00Z',
  to: formatJSDateToLocal(today) + 'T23:59:59Z',
  currency: 'DOP',
  provider: '',
  // Complete objects for metadata
  productObj: undefined,
  enterpriseObj: undefined,
  agencyObj: undefined,
  workstationObj: undefined,
  providerObj: undefined,
}

export const Filters: React.FC<{ onChange: (filters: FilterModel) => void; onSearch: (filters: FilterModel) => void; isLoading?: boolean }> = ({ onChange, onSearch, isLoading = false }) => {
  const [filters, setFilters] = useState<FilterModel>(initialFilters)
  const { getLotsalcomFileDownloadUri, file } = useLotsalcomFileDownloadUri()
  const [readyToDownload, setReadyToDownload] = useState(true)
  const [isDefaultEnterprise, setIsDefaultEnterprise] = useState(false)
  const [selectedEnterprise, setSelectedEnterprise] = useState<Enterprise | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({})
  // Removed workstation input state - using direct filter state instead
  

  const { label } = useAppTranslation()
  const { userEnterprise } = getAppBarState().appState

  // No initial filtering - all filtering is done through search button

  // No debouncing needed for workstationId - it's a direct input field

  // Don't initialize with default values - let user select manually

  useEffect(() => {
    if (file && filters.from && readyToDownload) {
      saveAs(file.sasUri, file.fileName)
    }
  }, [file, filters.from, readyToDownload])

  const handleDateChange = (date: Date) => {
    setReadyToDownload(false)
    setFilters({
      ...filters,
      from: formatJSDateToLocal(date) + 'T00:00:00Z',
      to: formatJSDateToLocal(date) + 'T23:59:59Z',
    })
  }

  const handleDownlaod = useCallback((date: string) => {
    getLotsalcomFileDownloadUri(date)
    setReadyToDownload(true)
  }, [])

  const handleSearch = useCallback(() => {
    // Use functional update to get the latest state
    setFilters(currentFilters => {
      // Validate required fields
      const requiredFields = {
        product: currentFilters.product,
        enterpriseId: currentFilters.enterpriseId,
        agencyId: currentFilters.agencyId,
        workstationId: currentFilters.workstationId,
        from: currentFilters.from,
        to: currentFilters.to
      }
      
      const errors: Record<string, boolean> = {}
      Object.entries(requiredFields).forEach(([key, value]) => {
        errors[key] = !value || value.trim() === ''
      })
      
      setValidationErrors(errors)
      
      const hasErrors = Object.values(errors).some(error => error)
      if (hasErrors) {
        return currentFilters
      }
      
      // Clear validation errors on successful validation
      setValidationErrors({})
      
      // Send filters to parent and trigger search
      onChange(currentFilters)
      onSearch(currentFilters)
      return currentFilters
    })
  }, [onChange, onSearch])

  // No agency fetching needed for simple TextField input
  
  return (
    <Card
      sx={{
        p: 1,
      }}
    >
      <Box p={1}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2} lg={2}>
            <DateDayControl onChange={handleDateChange} showButtons={false} label={label('FECHA')} />
            {validationErrors.from && (
              <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {label('REQUERIDO')}
              </Typography>
            )}
          </Grid>          
          <Grid item xs={12} md={2} lg={2}>
            <WorkstationProductSelect
              disableClearable={false}
              selectDefaultProduct={false}
              disabled={false}
              value={filters.product as WorkstationProductsEnum}
              onChange={(val) => {
                const productObj = val ? { value: val, label: val } : undefined;
                setFilters({
                  ...filters,
                  product: val || '',
                  productObj: productObj,
                });
                // Clear validation error when value changes
                setValidationErrors(prev => ({ ...prev, product: false }));
              }}
              getDefaultValue={(val) => {
                const productObj = val ? { value: val, label: val } : undefined;
                setFilters({
                  ...filters,
                  product: val || '',
                  productObj: productObj,
                });
                setValidationErrors(prev => ({ ...prev, product: false }));
              }}
            />
            {validationErrors.product && (
              <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {label('REQUERIDO')}
              </Typography>
            )}
          </Grid>
          {!isDefaultEnterprise && (
            <Grid item xs={12} md={2} lg={2}>
              <UserEnterpriseSelect selectDefaultEnterprise={false} onChangeValue={(value: Enterprise, action: string) => {
                  if (action === 'default') {
                    setIsDefaultEnterprise(true);
                    setSelectedEnterprise(value);
                    setFilters({
                      ...filters,
                      enterpriseId: value?.id || '',
                      enterpriseObj: value,
                      agencyId: '', // Reset agency when enterprise changes
                      agencyObj: undefined, // Reset agency object
                      workstationId: '', // Reset workstation when enterprise changes
                      workstationObj: undefined, // Reset workstation object
                      provider: '', // Set provider to empty string (TODOS) when enterprise is default
                    });
                  } else {
                    setIsDefaultEnterprise(false);
                    setSelectedEnterprise(value);
                    setFilters({
                      ...filters,
                      enterpriseId: value?.id || '',
                      enterpriseObj: value,
                      agencyId: '', // Reset agency when enterprise changes
                      agencyObj: undefined, // Reset agency object
                      workstationId: '', // Reset workstation when enterprise changes
                      workstationObj: undefined, // Reset workstation object
                    });
                  }
                  // Clear validation errors when value changes
                  setValidationErrors(prev => ({ ...prev, enterpriseId: false, agencyId: false, workstationId: false }));
                }}
              />
              {validationErrors.enterpriseId && (
                <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {label('REQUERIDO')}
                </Typography>
              )}
            </Grid>
          )}
          <Grid item xs={12} md={2} lg={2}>
            <AgencySelect
              key={selectedEnterprise?.id} // Force re-render when enterprise changes
              selectedEnterprise={selectedEnterprise}
              disableClearable={false}
              setDefault={false}
              getDefaultAgency={(agency) => {
                setFilters({
                  ...filters,
                  agencyId: agency?.id || '',
                  agencyObj: agency,
                  workstationId: '', // Reset workstation when agency changes
                  workstationObj: undefined, // Reset workstation object
                });
                setValidationErrors(prev => ({ ...prev, agencyId: false, workstationId: false }));
              }}
              onChangeValue={(value: Agency) => {
                setFilters({
                  ...filters,
                  agencyId: value?.id || '',
                  agencyObj: value,
                  workstationId: '', // Reset workstation when agency changes
                  workstationObj: undefined, // Reset workstation object
                });
                setValidationErrors(prev => ({ ...prev, agencyId: false, workstationId: false }));
              }}
            />
            {validationErrors.agencyId && (
              <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {label('REQUERIDO')}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <WorkstationSelect
              key={filters.agencyId} // Force re-render when agency changes
              locationId={filters.agencyId}
              onChangeValue={(value: Workstation) => {
                setFilters({
                  ...filters,
                  workstationId: value?.id || '',
                  workstationObj: value,
                });
                setValidationErrors(prev => ({ ...prev, workstationId: false }));
              }}
            />
            {validationErrors.workstationId && (
              <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {label('REQUERIDO')}
              </Typography>
            )}
          </Grid>
          {!isDefaultEnterprise && (
            <Grid item xs={12} md={2} lg={2}>
              <ProviderSelect
                disableClearable={false}
                value={filters.provider}
                onChange={(val) => {
                  const providerObj = val ? { value: val, label: val === 'GlobalToteTicketService' ? 'Global' : val === 'TotelinkTicketService' ? 'United' : val } : undefined;
                  setFilters({
                    ...filters,
                    provider: val || '',
                    providerObj: providerObj,
                  });
                }}
                getDefaultValue={(val) => {
                  const providerObj = val ? { value: val, label: val === 'GlobalToteTicketService' ? 'Global' : val === 'TotelinkTicketService' ? 'United' : val } : undefined;
                  setFilters({
                    ...filters,
                    provider: val || '',
                    providerObj: providerObj,
                  });
                }}
              />
            </Grid>
          )}         
          <Grid item xs={12} md={2} lg={2}>
            <SearchButton 
              color="primary" 
              onClick={handleSearch} 
              isLoading={isLoading}
              style={{ height: '56px', width: '100%' }}
            />
          </Grid>
          
        </Grid>
      </Box>
    </Card>
  )
}
